/**
 * Argon2id key derivation function.
 *
 * Same parameters must be used on both the pipeline (Node) and viewer
 * (browser) sides. Default parameters are committed here; do not change
 * them without a migration plan.
 *
 * Dependencies: hash-wasm (Argon2id implementation, works in Node + browser
 * via WASM without native bindings).
 */

import { argon2id } from 'hash-wasm';

export interface KdfParams {
  iterations: number;
  memoryKiB: number;
  parallelism: number;
  /** Output length in bytes. */
  outputLen: number;
}

export const DEFAULT_KDF_PARAMS: KdfParams = {
  iterations: 3,
  memoryKiB: 65536,  // 64 MiB — pipeline must use identical value
  parallelism: 1,
  outputLen: 32,     // 256-bit key for AES-256-GCM
};

// ---------------------------------------------------------------------------
// Passphrase strength check
// ---------------------------------------------------------------------------

export interface WeakPassphraseResult {
  /** true = hard block (must not proceed); false = at least a soft warning. */
  ok: boolean;
  /** Human-readable reason, present whenever ok is false. */
  reason?: string;
  /**
   * Integer strength score 0–100.
   * ≥70: strong; 40–69: acceptable (soft warning); <40: weak (hard block).
   */
  score: number;
}

/**
 * Hard-coded list of extremely common passphrase patterns.
 * This is intentionally small — the KDF provides the real protection.
 * The list just catches "password", "12345678", "qwerty" etc. that users
 * might accidentally type while testing.
 */
const COMMON_PATTERNS: readonly RegExp[] = [
  /^1234/,
  /^abcd/i,
  /^qwerty/i,
  /^password/i,
  /^passphrase/i,
  /^letmein/i,
  /^monkey/i,
  /^dragon/i,
  /^iloveyou/i,
  /^sunshine/i,
  /^princess/i,
  /^welcome/i,
  /^admin/i,
];

/**
 * Checks whether a passphrase is strong enough for use with this app.
 *
 * Policy:
 *  - Hard reject (ok: false) if length < 10.
 *  - Hard reject if the passphrase matches a common pattern from the list above.
 *  - Hard reject if entirely numeric (e.g. a PIN or phone number).
 *  - Soft warn (ok: false, score: 40–69) if entirely lowercase with no digits
 *    or symbols — technically allowed by the KDF but weak against offline attacks.
 *  - ok: true (score: 70–100) otherwise.
 *
 * NOTE: The KDF (Argon2id) provides the real equality check. Wrong passphrases
 * derive a different key which causes AES-GCM authentication to fail. This
 * function is UI-level guidance only and is NOT a security gate — the GCM
 * auth failure IS the gate.
 */
export function weakPassphraseCheck(pp: string): WeakPassphraseResult {
  if (pp.length < 10) {
    return {
      ok: false,
      score: Math.floor((pp.length / 10) * 39),  // 0–39 for < 10 chars
      reason: 'Passphrase must be at least 10 characters.',
    };
  }

  // Numeric-only check BEFORE common-pattern check so "1234567890" reports
  // the numeric reason, not the "starts with 1234" pattern reason.
  if (/^\d+$/.test(pp)) {
    return {
      ok: false,
      score: 20,
      reason: 'Numeric-only passphrases are not permitted. Add letters and symbols.',
    };
  }

  for (const pattern of COMMON_PATTERNS) {
    if (pattern.test(pp)) {
      return {
        ok: false,
        score: 15,
        reason: 'This passphrase is too common. Please choose something more unique.',
      };
    }
  }

  // Soft-reject: all lowercase, no digits or symbols
  if (/^[a-z]+$/.test(pp)) {
    return {
      ok: false,
      score: 55,
      reason:
        'Passphrase appears weak (all lowercase letters, no digits or symbols). ' +
        'Consider adding numbers, capitals, or punctuation.',
    };
  }

  // Score heuristic (purely advisory):
  //   70 base for passing hard checks + up to 30 for length and character variety.
  let score = 70;
  if (pp.length >= 16) score += 10;
  if (pp.length >= 24) score += 5;
  if (/[A-Z]/.test(pp)) score += 5;
  if (/\d/.test(pp)) score += 5;
  if (/[^A-Za-z0-9]/.test(pp)) score += 5;
  score = Math.min(score, 100);

  return { ok: true, score };
}

// ---------------------------------------------------------------------------
// Key derivation
// ---------------------------------------------------------------------------

/**
 * Derives a 32-byte symmetric key from a passphrase and salt using Argon2id.
 *
 * @param passphrase - The user-supplied passphrase. Minimum 10 characters
 *   enforced at the KDF layer (the UI should call weakPassphraseCheck first).
 * @param salt - Random salt (16–32 bytes recommended). Must be unique per
 *   artifact.
 * @param params - Optional override of default KDF parameters.
 */
export async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  params: Partial<KdfParams> = {},
): Promise<Uint8Array> {
  if (passphrase.length < 10) {
    throw new Error(
      'Passphrase must be at least 10 characters.',
    );
  }

  const p = { ...DEFAULT_KDF_PARAMS, ...params };

  const hashHex = await argon2id({
    password: passphrase,
    salt,
    iterations: p.iterations,
    memorySize: p.memoryKiB,
    parallelism: p.parallelism,
    hashLength: p.outputLen,
    outputType: 'hex',
  });

  // Convert hex string to Uint8Array
  const bytes = new Uint8Array(p.outputLen);
  for (let i = 0; i < p.outputLen; i++) {
    bytes[i] = parseInt(hashHex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
